/**
 * me/index.tsx — Student dashboard.
 *
 * Replaces the Phase B placeholder. The four widgets from N-05 surface
 * their real (typed) shapes backed by /api/progress/me.
 *
 * Routes:
 *   /me          — landing with progress + activity feed (this file)
 *   /me/progress — full-fidelity view with badge grid (later in this phase)
 *   /me/badges   — standalone badge grid (Fase 6 N-06 polish)
 */

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, Link, useNavigate } from '@tanstack/react-router';
import { ProgressHeader } from '../../components/student/ProgressHeader.js';
import { ClayIcon } from '../../components/student/ClayIcon.js';
import { GameProgressCard } from '../../components/student/GameProgressCard.js';
import { WorksheetProgressCard } from '../../components/student/WorksheetProgressCard.js';
import { RecentActivityFeed } from '../../components/student/RecentActivityFeed.js';
import { LiveQuizzesSection } from '../../components/student/LiveQuizzesSection.js';
import { StudentClassList } from '../../components/student/StudentClassList.js';
import {
  freshSessionQuery,
  myProgressQuery,
  myBadgesQuery,
  myClassContentQuery,
  type ClassContentEntry,
  type ClassMembership,
  type SessionPrincipal,
  type ProgressSummary,
} from '../../lib/api/queries.js';
import { activeRcSessionQuery } from '../../lib/api/queries-rc.js';

export const Route = createFileRoute('/me/')({
  beforeLoad: async ({ context, location }) => {
    // Force a fresh session fetch — same pattern as /me/progress. Without
    // this, /me relies on the root loader's cached data, which can be
    // empty on the first navigation (e.g., E2E tests).
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role === 'tutor' && user.isApprovedTutor) {
      throw redirect({ to: '/dashboard' });
    }
    if (user.role === 'parent') {
      // N-17: parent dashboard ships at /parent. Don't let parents see the
      // student dashboard — redirect them to their own home.
      throw redirect({ to: '/parent' });
    }
    return null;
  },
  loader: async ({ context }) => {
    await context.queryClient.prefetchQuery(myProgressQuery);
    return null;
  },
  component: StudentDashboard,
});

function StudentDashboard(): ReactNode {
  const navigate = useNavigate();

  // Fresh session (not getQueryData): after logout+login-as-another-user,
  // a stale cache could render the previous student's name ("Halo,
  // Siswa!"). Redirect to /login when the session is gone (401 →
  // ok===false), matching the beforeLoad guard client-side (BUG-SIS-05).
  const freshSession = useQuery({ ...freshSessionQuery });
  const payload = freshSession.data;
  const sessionData = payload?.ok ? payload.data : undefined;
  const sessionOk = sessionData !== undefined;
  const sessionResolved = freshSession.isSuccess || freshSession.isError;

  useEffect(() => {
    if (sessionResolved && !sessionOk) {
      void navigate({ to: '/login', search: { error: 'session', redirect: '/me' } });
    }
  }, [sessionResolved, sessionOk, navigate]);

  const studentName = sessionData?.user?.name ?? 'Siswa';
  const memberships = sessionData?.memberships ?? [];

  // N-13 Opsi A: materi (game/worksheet) yang dibuka tutor untuk kelas
  // siswa — ditampilkan di kartu kelas di bawah.
  const classContentQuery = useQuery({ ...myClassContentQuery, enabled: sessionOk });
  const classContent: Record<string, ClassContentEntry> = {};
  if (classContentQuery.data?.classes) {
    for (const entry of classContentQuery.data.classes) {
      classContent[entry.classId] = entry;
    }
  }

  const activeSessionQueries = useQueries({
    queries: memberships.map((membership) => activeRcSessionQuery(membership.classId)),
  });
  const activeSessions: Record<string, string> = {};
  memberships.forEach((membership, index) => {
    const response = activeSessionQueries[index]?.data;
    if (response?.ok && response.data.session) {
      activeSessions[membership.classId] = response.data.session.id;
    }
  });

  // useQuery drives the live subscription; the loader's prefetch warms the
  // cache so the first paint shows data immediately.
  const liveQuery = useQuery(myProgressQuery);
  const badgesQuery = useQuery(myBadgesQuery());
  const recentBadges = badgesQuery.data?.ok
    ? badgesQuery.data.data.earned
        .slice()
        .sort((a, b) => (b.awardedAt ?? '').localeCompare(a.awardedAt ?? ''))
        .slice(0, 3)
    : [];
  const progress: ProgressSummary | null = liveQuery.data?.ok ? liveQuery.data.data : null;

  const aggregate = progress?.aggregate ?? {
    gamesStarted: 0,
    gamesCompleted: 0,
    gameStars: 0,
    quizStars: 0,
    totalStars: 0,
    quizzesCompleted: 0,
    worksheetsCompleted: 0,
    totalWorksheets: 0,
    streakDays: 0,
    badgesEarned: 0,
  };
  const games = progress?.games ?? [];
  const worksheets = progress?.worksheets ?? [];
  const recent = progress?.recentActivity ?? [];

  return (
    <main className="page-shell" data-testid="me-dashboard">
      <ProgressHeader studentName={studentName} aggregate={aggregate} recentBadges={recentBadges} />

      <StudentClassList
        memberships={memberships}
        classActivity={progress?.classActivity ?? []}
        activeSessions={activeSessions}
        classContent={classContent}
      />

      <section className="section" aria-labelledby="next-action">
        <h2 className="section-title" id="next-action">
          Langkah berikutnya
        </h2>
        <div className="cluster">
          <Link className="button button-secondary" to="/me/progress">
            Lihat progres lengkap
          </Link>
          <Link className="button button-secondary" to="/me/badges">
            Lihat badge
          </Link>
          <Link className="button button-ghost" to="/games">
            Cari game publik
          </Link>
        </div>
      </section>

      <section className="section" aria-labelledby="games-section">
        <h2 className="section-title" id="games-section">
          Progres game
        </h2>
        {games.length > 0 ? (
          <div className="card-grid">
            {games.map((entry) => (
              <GameProgressCard key={entry.gameId} entry={entry} />
            ))}
          </div>
        ) : (
          <div className="pixel-empty" data-testid="pixel-empty-games">
            <ClayIcon name="joystick" className="pixel-empty-icon" />
            <p className="form-message" style={{ color: 'var(--color-ink-300)' }}>
              Belum ada game yang dimulai. Coba <Link to="/games">katalog game publik</Link> untuk
              mulai.
            </p>
          </div>
        )}
      </section>

      <LiveQuizzesSection />

      <section className="section" aria-labelledby="worksheets-section">
        <h2 className="section-title" id="worksheets-section">
          Worksheet
        </h2>
        {worksheets.length > 0 ? (
          <div className="card-grid">
            {worksheets.map((entry) => (
              <WorksheetProgressCard key={entry.worksheetId} entry={entry} />
            ))}
          </div>
        ) : (
          <div className="pixel-empty" data-testid="pixel-empty-worksheets">
            <ClayIcon name="scroll" className="pixel-empty-icon" />
            <p className="form-message" style={{ color: 'var(--color-ink-300)' }}>
              Belum ada worksheet yang dikerjakan. Cek Materi kelas di kartu kelas di atas untuk
              membuka worksheet dari tutor.
            </p>
          </div>
        )}
      </section>

      <section className="section" aria-labelledby="activity-section">
        <h2 className="section-title" id="activity-section">
          Aktivitas terbaru
        </h2>
        <RecentActivityFeed events={recent} limit={6} />
      </section>
    </main>
  );
}
